import React, { useCallback, useEffect, useMemo, useRef, useReducer } from 'react';
import type { ScrollView, View } from 'react-native';
import {
  Image,
  ImageProps,
  Pressable,
  StyleSheet,
  TextStyle,
  ViewStyle,
} from 'react-native';
import { findDOMNode } from 'react-dom';
import { isIOS } from 'react-device-detect';
import { Page, Sentiment, PresentedMeta } from '@alucio/aws-beacon-amplify/src/models';
import {
  DNABox,
  DNAButton,
  DNACheckbox,
  DNAIcon,
  DNAText,
  Iffy,
  responsive,
  Stack,
  useSafePromise,
  util,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/lib/theming/themes/alucio/colors';
import {
  SlidePreviewModal,
  DNAChip,
  DNAPopover,
  DNAModalActions,
  useInView,
} from 'src/components/DNA';
import { useDispatch } from 'src/state/redux';
import { DocumentVersionORM } from 'src/types/orms';
import { getImageObjectURLFromCloudfront, retryPromise } from 'src/utils/loadCloudfrontAsset/common';
import { ThumbnailSize, thumbnailSizeDimensions } from 'src/hooks/useThumbnailSize/useThumbnailSize';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';

export type MagnifyConfig = {
  enabled: boolean;
  disableNavigation?: boolean;
  buttonStyle?: ViewStyle;
  onMagnify?: () => void;
}

export enum MagnifyPreset {
  NONE = 'none',
  WITH_NAVIGATION = 'with_navigation',
  SINGLE = 'single',
}

enum DNAThumbnailModes {
  READ_ONLY,
  SELECTABLE,
  REMOVABLE,
  REMOVABLE_AND_PREVIEW,
}

enum DNAThumbnailVariants {
  DEFAULT,
  ASSOCIATED,
  INFO,
  MATCH_SLIDE,
  MEETING,
}

// Magnify Presets - Immutable configurations for performance
export const MagnifyPresets: Record<MagnifyPreset, Readonly<MagnifyConfig>> = {
  [MagnifyPreset.NONE]: Object.freeze({
    enabled: false,
  }),
  [MagnifyPreset.WITH_NAVIGATION]: Object.freeze({
    enabled: true,
    disableNavigation: false,
    buttonStyle: { marginTop: 8, marginRight: 10 },
  }),
  [MagnifyPreset.SINGLE]: Object.freeze({
    enabled: true,
    disableNavigation: true,
    buttonStyle: { marginTop: 8, marginRight: 10 },
  }),
};

// Helper function with cache for normalizing magnify config
const normalizedConfigCache = new Map<string, MagnifyConfig>();

const normalizeMagnifyConfig = (
  config?: MagnifyConfig | MagnifyPreset | string,
): MagnifyConfig => {
  if (!config) return MagnifyPresets[MagnifyPreset.NONE];

  if (typeof config === 'string') {
    if (!normalizedConfigCache.has(config)) {
      const preset = MagnifyPresets[config as MagnifyPreset];
      normalizedConfigCache.set(
        config,
        preset || MagnifyPresets[MagnifyPreset.NONE],
      );
    }
    return normalizedConfigCache.get(config)!;
  }

  return config;
};

type ComposableComponentStyles =
  | 'thumbnailContainer'
  | 'loadedThumbnail'
  | 'pageNumber'
  | 'borderSelected'
  | 'borderWarning'
  | 'borderSuccess'

type ComposableStyle = Record<ComposableComponentStyles, ViewStyle | TextStyle>

const defaultVariantStyle: ComposableStyle = {
  thumbnailContainer: {},
  loadedThumbnail: {
    backgroundColor: colors['color-gray-100'],
    borderRadius: 3,
    borderWidth: 1,
    borderColor: colors['color-ui2-neutral-200'],
  },
  borderSelected: {
    borderColor: colors['color-brand2-500'],
  },
  borderWarning: {
    borderColor: colors['color-warning-500'],
  },
  pageNumber: {
    color: colors['color-text-basic'],
  },
  borderSuccess: {
    borderColor: colors['color-success-500'],
  },
}

export const variantStyles: Record<DNAThumbnailVariants, ComposableStyle> = {
  [DNAThumbnailVariants.DEFAULT]: {
    ...defaultVariantStyle,
  },
  [DNAThumbnailVariants.INFO]: {
    ...defaultVariantStyle,
    thumbnailContainer: {
      borderColor: colors['color-ui2-neutral-200'],
      borderRadius: 3,
      borderWidth: 1,
    },
  },
  [DNAThumbnailVariants.ASSOCIATED]: {
    ...defaultVariantStyle,
    thumbnailContainer: {
      borderColor: colors['color-gray-100'],
      borderRadius: 3,
      borderWidth: 1,
    },
  },
  [DNAThumbnailVariants.MATCH_SLIDE]: {
    ...defaultVariantStyle,
    thumbnailContainer: {
      borderColor: colors['color-gray-100'],
      borderRadius: 3,
      borderWidth: 1,
    },
  },
  [DNAThumbnailVariants.MEETING]: {
    ...defaultVariantStyle,
    loadedThumbnail: {
      ...defaultVariantStyle.loadedThumbnail,
      borderColor: colors['color-border-dark-secondary'],
    },
  },
}

const cornerItemsStyles = StyleSheet.create<Record<ThumbnailSize, any>>({
  xxs: {
    margin: 6,
  },
  xs: {
    margin: 6,
  },
  sm: {
    margin: 8,
  },
  md: {
    margin: 10,
  },
  lg: {
    margin: 12,
  },
  xl: {
    margin: 16,
  },
  xxl: {
    margin: 18,
  },
})

const S = StyleSheet.create({
  fullW: { width: '100%' },
  opaque: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
  overlayText: {
    paddingVertical: 2,
    paddingHorizontal: 4,
    marginHorizontal: 2,
    paddingBottom: 5,
    borderBottomLeftRadius: 2,
    borderBottomRightRadius: 2,
  },
  pageNumber: {
    backgroundColor: colors['color-gray-300'],
    color: colors['color-text-basic'],
    paddingVertical: 2,
    paddingHorizontal: 4,
    borderRadius: 2,
  },
  disabledPopover: {
    color: colors['color-text-white'],
    marginHorizontal: 12,
    width: 400,
  },
  matchSlidePopover:{
    color: colors['color-text-white'],
    marginHorizontal: 2,
    marginVertical: 2,
  },
  presentedMetaWrapper: {
    borderRadius: 4,
    backgroundColor: colors['color-black'],
    padding: 4,
  },
  topRightIconButton: {
    marginTop: 4,
    marginRight: 4,
  },
  hiddenOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  hiddenIconWrapper: {
    marginTop: 6,
    marginLeft: 6,
  },
})

export interface DNAThumbnailProps extends Omit<ImageProps, 'source'> {
  useLoadingIndicator?: boolean,
  s3URL?: string,
  children?: never,
  overlayText?: string,
  size?: ThumbnailSize,
  presentedMeta?: PresentedMeta,
  hasNotation?: boolean,
  height?: number,
  width?: number,
  unavailableContent?: boolean,

  pageNumber?: number,
  isCover?: boolean,
  isRequired?: boolean,
  isHidden?: boolean,
  sourceIsHidden?: boolean,
  isTextInsertionEnabled?: boolean,

  mode?: DNAThumbnailModes,
  variant?: DNAThumbnailVariants,

  status?: 'warning' | 'success',
  checked?: boolean,
  onCheck?: () => void,
  onCheckIn?: () => void,
  onLongPress?: () => void,
  onClose?: () => void,
  onRevert?: () => void,
  delayLongPress?: number,

  disabled?: boolean,
  disabledMessage?: string,
  preview?: { pages: Page[], docVerORM: DocumentVersionORM, getSlideTitle?: (number: number) => string },
  onEditPageData?: (pageNumber?: number, s3URL?: string) => void,
  showEditButton?: boolean,
  showSpeakerNotesButton?: boolean,
  thumbnailTitle?: string,

  deferLoad?: boolean,
  magnifyConfig?: MagnifyConfig | MagnifyPreset | string
}

export type imageURI = {
  uri: string,
  width: number,
  height: number,
  ratio: number,
}

export enum FetchStatus {
  PENDING,
  RESOLVED,
  REJECTED,
}

type UseImageLoader = {
  imageURI: imageURI | undefined;
  fetchStatus: FetchStatus | undefined;
  showLoadingIndicator: boolean;
}

type UseImageLoaderState = {
  imageURI: imageURI | undefined;
  fetchStatus: FetchStatus | undefined;
  currentS3URL: string | undefined;
}

type UseImageLoaderActions = (
  | {
      type: 'SET_IMAGE_URI',
      payload: {
        imageURI: imageURI | undefined,
      }
    }
  | {
      type: 'SET_FETCH_STATUS',
      payload: {
        fetchStatus: FetchStatus | undefined,
      }
    }
  | {
      type: 'SET_CURRENT_S3_URL',
      payload: {
        currentS3URL: string | undefined,
      }
    }
  | {
      type: 'SET_IMAGE_URI_AND_FETCH_STATUS_AND_CURRENT_S3_URL',
      payload: {
        imageURI: imageURI | undefined,
        fetchStatus: FetchStatus | undefined,
        currentS3URL: string | undefined,
      }
    }
  | {
      type: 'SET_IMAGE_URI_AND_FETCH_STATUS',
      payload: {
        imageURI: imageURI | undefined,
        fetchStatus: FetchStatus | undefined,
      }
    }
)

const useImageLoader = (props: DNAThumbnailProps): UseImageLoader => {
  const [state, dispatch] = useReducer(
    // [TODO-TS] - Can use generics for useReducer<R, T, Init> instead
    (state: UseImageLoaderState, actions: UseImageLoaderActions): UseImageLoaderState => {
      switch (actions.type) {
        case 'SET_IMAGE_URI':
          return {
            ...state,
            imageURI: actions.payload.imageURI,
          }
        case 'SET_FETCH_STATUS':
          return {
            ...state,
            fetchStatus: actions.payload.fetchStatus,
          }
        case 'SET_CURRENT_S3_URL':
          return {
            ...state,
            currentS3URL: actions.payload.currentS3URL,
          }
        case 'SET_IMAGE_URI_AND_FETCH_STATUS':
          return {
            ...state,
            imageURI: actions.payload.imageURI,
            fetchStatus: actions.payload.fetchStatus,
          }
        case 'SET_IMAGE_URI_AND_FETCH_STATUS_AND_CURRENT_S3_URL': {
          return {
            ...state,
            imageURI: actions.payload.imageURI,
            fetchStatus: actions.payload.fetchStatus,
            currentS3URL: actions.payload.currentS3URL,
          }
        }
        default:
          return state
      }
    },
    {
      imageURI: undefined,
      fetchStatus: undefined,
      currentS3URL: undefined,
    },
  )

  const safePromise = useSafePromise()

  const {
    s3URL,
    useLoadingIndicator = true,
    deferLoad = false,
  } = props
  const s3URLReference = useRef<string | undefined>(s3URL);

  useEffect(
    () => {
      // Explicitly release the image's reference on unmount
      //  to garbage collect (hopefully)
      // [TODO] - Consider not releasing if storing multiple thumbnail sizes?
      return () => {
        if (state.imageURI?.uri) {
          URL.revokeObjectURL(state.imageURI.uri)
        }
      }
    },
    [state.imageURI],
  )

  async function fetchImage() {
    if (!s3URL) {
      dispatch({
        type: 'SET_FETCH_STATUS',
        payload: {
          fetchStatus: FetchStatus.REJECTED,
        },
      })
      return;
    }

    if (state.currentS3URL && s3URL === state.currentS3URL) {
      return;
    }

    try {
      dispatch({
        type: 'SET_FETCH_STATUS',
        payload: {
          fetchStatus: FetchStatus.PENDING,
        },
      })

      // [TODO] - Make the `makeSafe` promise infer the return type from the param instead of `as` assertion
      const objURL = await retryPromise(
        safePromise.makeSafe(
          getImageObjectURLFromCloudfront(s3URL),
        ),
      ) as string

      const fetchDimensions = () =>
        new Promise<{ width: number, height: number }>(
          (resolve, reject) => (
            Image.getSize(
              objURL,
              (width, height) => resolve({ width, height }),
              reject,
            )
          ),
        )

      const dimensions = await retryPromise(safePromise.makeSafe(fetchDimensions()))

      dispatch({
        type: 'SET_IMAGE_URI_AND_FETCH_STATUS_AND_CURRENT_S3_URL',
        payload: {
          imageURI: {
            uri: objURL,
            ratio: responsive.dimensions.screenWidth / dimensions.width,
            ...dimensions,
          },
          fetchStatus: FetchStatus.RESOLVED,
          currentS3URL: s3URL,
        },
      })

      /** TODO: Probably a better way to structure this */
    } catch (err: any) {
      if (err?.isCanceled) return;
      dispatch({
        type: 'SET_IMAGE_URI_AND_FETCH_STATUS',
        payload: {
          imageURI: undefined,
          fetchStatus: FetchStatus.REJECTED,
        },
      })
    }
  }

  const showLoadingIndicator = (
    (state.fetchStatus === FetchStatus.PENDING) &&
    useLoadingIndicator
  )

  useEffect(
    () => {
      if (!deferLoad && (!state.fetchStatus || s3URLReference.current !== s3URL)) {
        fetchImage()
        s3URLReference.current = s3URL;
      }
    },
    [s3URL, state.imageURI, deferLoad],
  )

  return {
    imageURI: state.imageURI,
    fetchStatus: state.fetchStatus,
    showLoadingIndicator,
  }
}

/** This component renders presentedMeta & notation icons */
const OverlayIconComponent = (props: { presentedMeta?: PresentedMeta, hasNotation?: boolean }) => {
  const { presentedMeta, hasNotation } = props;
  const featureFlags = useFeatureFlags('enableMeetingNotes', 'enableMeetingReactions', 'enableMeetingFollowUp');
  const note = presentedMeta?.note
  const followUp = presentedMeta?.followUp
  const sentiment = presentedMeta?.sentiment

  // IN CASE THE PRESENTEDMETA EXISTS WITH EMPTY NOTES
  if ((!note || !featureFlags.enableMeetingNotes) &&
    (!followUp || !featureFlags.enableMeetingFollowUp) &&
    (!sentiment || !featureFlags.enableMeetingReactions) &&
    (!hasNotation)) {
    return <></>;
  }

  return (
    <DNABox style={S.presentedMetaWrapper} spacing="xs" alignY="center">
      <Iffy is={featureFlags.enableMeetingReactions && sentiment}>
        <DNAIcon.Styled
          status={sentiment === Sentiment.NEGATIVE ? 'warning' : 'success'}
          name={sentiment === Sentiment.NEGATIVE ? 'caret-down' : 'caret-up'}
        />
      </Iffy>
      <Iffy is={featureFlags.enableMeetingNotes && note}>
        <DNAIcon.Styled name="note-pencil" colorMode="dark" status="subtle" />
      </Iffy>
      <Iffy is={featureFlags.enableMeetingFollowUp && followUp}>
        <DNAIcon.Styled name="flag-fill" colorMode="dark" status="subtle" />
      </Iffy>
      <Iffy is={hasNotation}>
        <DNAIcon.Styled name="note-blank" colorMode="dark" status="subtle" />
      </Iffy>
    </DNABox>
  );
};

export type DNAThumbnailCore = React.MemoExoticComponent<
  React.ForwardRefExoticComponent<DNAThumbnailProps & React.RefAttributes<any>>
>
export type DNAThumbnailExtensions = {
  Modes: typeof DNAThumbnailModes,
  Variants: typeof DNAThumbnailVariants,
  MagnifyPresets: typeof MagnifyPreset,
  Lazy: typeof DNAThumbnailLazy
}

export type DNAThumbnail = DNAThumbnailCore & DNAThumbnailExtensions

/*
  * container is a ref to the container that wraps an element that needs to disable touch callout
  * right now only applies to IOS
*/
const useDisableTouchCallout = (container: React.RefObject<View>) => {
  useEffect(
    () => {
      if (container.current && isIOS) {
        // apply css to image
        const containerElement = container.current
        const imageElement = findDOMNode(containerElement) as HTMLElement
        // @ts-ignore apply -webkit-touch-callout: none to prevent long press menu
        imageElement.style.webkitTouchCallout = 'none'
        // apply -webkit-user-select: none to prevent long press menu
        imageElement.style.webkitUserSelect = 'none'
      }
    }, [container.current])
}

// [NOTE] - https://stackoverflow.com/questions/59764130/adding-non-nullable-static-type-to-memoized-components-throws-lint-error-see-ex
const DNAThumbnailCore: DNAThumbnailCore = React.memo(React.forwardRef<any, DNAThumbnailProps>(
  (props, ref) => {
    const {
      s3URL,
      height: propHeight,
      width: propWidth,
      overlayText,
      size,
      pageNumber,
      isCover,
      isRequired,
      isHidden,
      sourceIsHidden,
      unavailableContent,
      isTextInsertionEnabled,

      mode = DNAThumbnailModes.READ_ONLY,
      variant = DNAThumbnailVariants.DEFAULT,
      presentedMeta,
      hasNotation,
      onCheck,
      onCheckIn,
      onLongPress,
      onClose,
      onRevert,
      magnifyConfig: magnifyConfigProp,
      delayLongPress,
      status,
      checked,
      disabled,
      resizeMode = 'center',
      disabledMessage,
      preview,
      onEditPageData,
      showEditButton,
      thumbnailTitle,
      showSpeakerNotesButton,
      deferLoad = false,
      ...rest
    } = props
    const dispatch = useDispatch()

    // Normalize and memoize magnify configuration with backward compatibility
    const magnifyConfig = useMemo(() => {
      const normalized = normalizeMagnifyConfig(magnifyConfigProp);
      return normalized;
    }, [magnifyConfigProp]);

    // Internal magnify handler that opens the preview modal
    const handleInternalMagnify = useCallback(() => {
      if (!preview || !magnifyConfig.enabled) return;

      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        component: (modalProps) => (
          <SlidePreviewModal
            pageNumber={pageNumber}
            payload={preview}
            disableNavigation={magnifyConfig.disableNavigation}
            {...modalProps}
          />
        ),
      }));
    }, [
      preview,
      pageNumber,
      magnifyConfig.enabled,
      magnifyConfig.disableNavigation,
      dispatch,
    ]);

    // Effective handler: custom handler takes precedence over internal handler
    const effectiveMagnifyHandler = useMemo(() => {
      if (magnifyConfig.onMagnify) return magnifyConfig.onMagnify;
      if (magnifyConfig.enabled) return handleInternalMagnify;
      return undefined;
    }, [magnifyConfig.onMagnify, magnifyConfig.enabled, handleInternalMagnify]);

    // Handle magnify button click with event propagation control
    const handleMagnifyClick = useCallback((e?: any) => {
      e?.stopPropagation?.();
      if (effectiveMagnifyHandler) {
        effectiveMagnifyHandler();
      }
    }, [effectiveMagnifyHandler]);

    const dimensions = size ? thumbnailSizeDimensions[size] : undefined

    const width = dimensions ? dimensions.width : propWidth
    const height = dimensions ? dimensions.height : propHeight
    const minSize = useMemo(
      () => ({
        minHeight: height,
        minWidth: width,
      }),
      [width, height],
    )
    const { imageURI, showLoadingIndicator, fetchStatus } = useImageLoader(props)

    // [TODO] - Consider having modifiers (status, checked) be rolled
    //          into the single stylesheet that's returned
    //          So we don't have to do inline style application at the prop level
    const variantStyle = variantStyles[variant]
    const badgesWidth = isCover && isRequired ? 180 : isCover || isRequired ? 105 : 50

    const imageRef = useRef<View>(null)
    useDisableTouchCallout(imageRef)

    const InteractiveWrapper = mode === DNAThumbnailModes.SELECTABLE
      ? Pressable
      : DNABox

    const interactiveProps = mode === DNAThumbnailModes.SELECTABLE
      ? {
        onPress: !disabled ? onCheck : undefined,
        onPressIn: !disabled ? onCheckIn : undefined,
        onLongPress: !disabled ? onLongPress : undefined,
        delayLongPress,
        ref: !disabled ? imageRef : undefined,
      }
      : {}

    const hideMagnifier = useMemo(() =>
      !pageNumber && magnifyConfig.onMagnify === undefined, [pageNumber, magnifyConfig.onMagnify]);

    if (deferLoad) {
      // Empty (appropriately sized) placeholder
      // [TODO] - Consider styling the placeholder
      return (<InteractiveWrapper
        ref={ref}
        style={{
          ...minSize,
          // we need to have the exact with of the thumbnail to avoid having a flick in the screen
          // when the intersection observer is triggered and changed the mock thumbnail for the real one
          // this is because the real thumbnail has a border and the mock one doesn't
          // so we need to add the border as a margin to the mocked thumbnail
          margin: variantStyle.loadedThumbnail.borderWidth,
        }}
      />)
    }

    return (
      <InteractiveWrapper style={minSize} {...interactiveProps}>
        <DNABox
          testID="document-thumbnail"
          appearance="col"
          spacing={pageNumber ? 'xs' : undefined}
        >
          <Stack anchor="bottom">

            {/* THUMBNAIL IMAGE */}
            <Stack.Layer>
              <Iffy is={preview}>
                <DNABox
                  style={util.mergeStyles(
                    undefined,
                    variantStyle.thumbnailContainer,
                    [variantStyle.borderSelected, checked],
                  )}
                >
                  {/* LOADING INDICATOR */}
                  <Iffy is={showLoadingIndicator}>
                    <DNABox style={minSize} onLayout={rest.onLayout}/>
                  </Iffy>

                  {/* LOADED */}
                  <Iffy is={fetchStatus === FetchStatus.RESOLVED}>
                    <Image
                      {...rest}
                      style={util.mergeStyles(
                        props,
                        variantStyle.loadedThumbnail,
                        { height, width },
                      )}
                      source={{ uri: imageURI?.uri }}
                      resizeMode={resizeMode}
                    />
                  </Iffy>

                  {/* FAILED IMAGE */}
                  <Iffy is={fetchStatus === FetchStatus.REJECTED}>
                    <DNABox style={minSize} />
                  </Iffy>
                </DNABox>
              </Iffy>
              <Iffy is={!preview}>
                <DNABox
                  style={util.mergeStyles(
                    undefined,
                    variantStyle.thumbnailContainer,
                    [variantStyle.borderWarning, status === 'warning'],
                    [variantStyle.borderSuccess, status === 'success'],
                    [variantStyle.borderSelected, checked],
                  )}
                >
                  {/* LOADING INDICATOR */}
                  <Iffy is={showLoadingIndicator && !unavailableContent}>
                    <DNABox style={minSize} onLayout={rest.onLayout}/>
                  </Iffy>

                  {/* LOADED */}
                  <Iffy is={fetchStatus === FetchStatus.RESOLVED && !unavailableContent}>
                    <Image
                      {...rest}
                      style={util.mergeStyles(
                        props,
                        variantStyle.loadedThumbnail,
                        { height, width },
                      )}
                      source={{ uri: imageURI?.uri }}
                      resizeMode={resizeMode}
                    />
                  </Iffy>

                  {/* FAILED IMAGE */}
                  <Iffy is={fetchStatus === FetchStatus.REJECTED && !unavailableContent}>
                    <DNABox style={minSize} />
                  </Iffy>

                  {/* UNAVAILABLE CONTENT */}
                  <Iffy is={unavailableContent}>
                    <DNABox
                      alignY="center"
                      alignX="center"
                      style={util.mergeStyles(
                        undefined,
                        minSize,
                        { backgroundColor: colors['color-gray-transparent-40'] },
                      )}
                    >
                      <DNAIcon.Styled
                        name="cloud-slash"
                        width={width && width > 200 ? 48 : 24}
                        height={width && width > 200 ? 32 : 16}
                      />
                    </DNABox>
                  </Iffy>
                </DNABox>
              </Iffy>
            </Stack.Layer>

            {/* HIDDEN SLIDE OVERLAY */}
            {(isHidden || sourceIsHidden) && (
              <Stack.Layer anchor="topLeft" fill>
                <DNABox fill style={S.hiddenOverlay} />
              </Stack.Layer>
            )}
            <Stack.Layer
              anchor="topLeft"
              style={[
                S.hiddenIconWrapper,
                mode === DNAThumbnailModes.SELECTABLE && { marginLeft: 38, marginTop: 11 },
              ]}
            >
              <Iffy is={isHidden || sourceIsHidden}>
                <DNAIcon.Styled
                  name="prohibit"
                  colorMode="dark"
                  color={colors['color-black']}
                />
              </Iffy>
            </Stack.Layer>

            {/* CHECKBOX */}
            <Stack.Layer anchor="topLeft">
              <Iffy is={mode === DNAThumbnailModes.SELECTABLE}>
                <Pressable style={cornerItemsStyles[size ?? 'md']}>
                  {/* DISABLED DNAPOPOVER */}
                  <Iffy is={disabled}>
                    <DNAPopover >
                      <DNAPopover.Anchor>
                        <DNACheckbox
                          context="altBg"
                          checked={!!checked}
                          disabled={disabled}
                          onChange={onCheck}
                        />
                      </DNAPopover.Anchor>
                      <DNAPopover.Content>
                        <DNAText
                          style={S.disabledPopover}
                        >
                          {disabledMessage}
                        </DNAText>
                      </DNAPopover.Content>
                    </DNAPopover>
                  </Iffy>

                  {/* REGULAR CHECKBOX */}
                  <Iffy is={!disabled}>
                    <DNACheckbox
                      context="altBg"
                      checked={!!checked}
                      disabled={disabled}
                      onChange={onCheck}
                      testID={checked ? 'checkbox-checked' : 'checkbox-unchecked'}
                    />
                  </Iffy>
                </Pressable>
              </Iffy>
            </Stack.Layer>

            {/* PRESENTEDMETA */}
            <Stack.Layer anchor="bottomRight" style={{ padding: 3 }}>
              <Iffy is={presentedMeta || hasNotation}>
                <OverlayIconComponent presentedMeta={presentedMeta} hasNotation={hasNotation} />
              </Iffy>
            </Stack.Layer>

            {/* OVERLAY TEXT */}
            <Stack.Layer style={S.fullW}>
              <Iffy is={overlayText && overlayText !== ''}>
                <DNABox fill style={[S.overlayText, S.opaque]}>
                  <DNAText colorMode="dark" numberOfLines={2} c1>
                    {overlayText}
                  </DNAText>
                </DNABox>
              </Iffy>
            </Stack.Layer>

            {/* REMOVEABLE CLOSE ICON */}
            <Stack.Layer anchor="topRight">
              <Iffy is={mode === DNAThumbnailModes.REMOVABLE}>
                <DNAButton
                  status="transparent"
                  iconLeft="x"
                  onPress={DNAThumbnail.Modes.REMOVABLE ? onCheck : undefined}
                  style={cornerItemsStyles[size ?? 'md']}
                />
              </Iffy>
            </Stack.Layer>

            {/* MAGNIFY BUTTON */}
            <Stack.Layer anchor="topRight">
              <DNABox spacing="xs" appearance="col">
                <Iffy is={magnifyConfig.enabled && !unavailableContent && !hideMagnifier}>
                  <DNAPopover placement="left">
                    <DNAPopover.Anchor>
                      <DNAButton
                        status="transparent"
                        iconLeft="magnifying-glass-plus"
                        onPress={handleMagnifyClick}
                        style={magnifyConfig.buttonStyle || S.topRightIconButton}
                      />
                    </DNAPopover.Anchor>
                    <DNAPopover.Content>
                      <DNAText style={S.matchSlidePopover}>
                        View larger
                      </DNAText>
                    </DNAPopover.Content>
                  </DNAPopover>
                  <Iffy is={!!onRevert}>
                    <DNAPopover placement="left">
                      <DNAPopover.Anchor>
                        <DNAButton
                          status="transparent"
                          iconLeft="arrow-u-up-left"
                          onPress={onRevert}
                          style={S.topRightIconButton}
                        />
                      </DNAPopover.Anchor>
                      <DNAPopover.Content>
                        <DNAText style={S.matchSlidePopover}>
                          Revert to original
                        </DNAText>
                      </DNAPopover.Content>
                    </DNAPopover>
                  </Iffy>
                  <Iffy is={mode === DNAThumbnailModes.REMOVABLE_AND_PREVIEW}>
                    <DNAPopover placement="left">
                      <DNAPopover.Anchor>
                        <DNAButton
                          status="transparent"
                          iconLeft="trash"
                          onPress={DNAThumbnail.Modes.REMOVABLE_AND_PREVIEW ? onClose : undefined}
                          style={S.topRightIconButton}
                        />
                      </DNAPopover.Anchor>
                      <DNAPopover.Content>
                        <DNAText style={S.matchSlidePopover}>
                          Remove
                        </DNAText>
                      </DNAPopover.Content>
                    </DNAPopover>
                  </Iffy>
                </Iffy>
              </DNABox>
            </Stack.Layer>

            {/* ASSOCIATED SLIDE */}
            <Stack.Layer anchor="bottomRight" style={cornerItemsStyles[size ?? 'md']}>
              <Iffy is={variant === DNAThumbnailVariants.ASSOCIATED}>
                <DNAText
                  appearance="outline"
                  style={[S.pageNumber]}
                >
                  {pageNumber}
                </DNAText>
              </Iffy>
            </Stack.Layer>
          </Stack>

          {/* SLIDE NUMBER & BADGE */}
          <Iffy is={(pageNumber !== undefined || isCover || isRequired)}>
            <DNABox testID="slide-title-container" alignY="start">
              <Iffy is={variant !== DNAThumbnailVariants.ASSOCIATED}>
                {/* PAGE NUMBER */}
                <DNAPopover placement="top">
                  <DNAPopover.Anchor>
                    <DNAText
                      testID="slide-number"
                      numberOfLines={1}
                      bold
                      style={{
                        maxWidth: (width ?? 100) - badgesWidth,
                        color: colors['color-gray-400'],
                      }}
                      status="subtle"
                    >
                      {`${pageNumber}. ${thumbnailTitle ?? `Slide ${pageNumber}`}`}
                    </DNAText>
                  </DNAPopover.Anchor>
                  <DNAPopover.Content>
                    <DNAText
                      colorMode="dark"
                      style={{ maxWidth: 420 }}
                    >
                      {thumbnailTitle ?? `Slide ${pageNumber}`}
                    </DNAText>
                  </DNAPopover.Content>
                </DNAPopover>
              </Iffy>
              <DNABox fill alignX="end" spacing="sm">
                <Iffy is={showEditButton}>
                  <DNABox fill alignX="end" spacing="sm">
                    <Iffy is={showSpeakerNotesButton}>
                      <DNAButton
                        appearance="ghost"
                        status="tertiary"
                        iconRight="article"
                        onPress={() => onEditPageData?.(pageNumber, s3URL)}
                      />
                    </Iffy>
                    <DNAButton
                      appearance="ghost"
                      status="tertiary"
                      iconRight="pencil-simple"
                      onPress={() => onEditPageData?.(pageNumber, s3URL)}
                    />
                  </DNABox>
                </Iffy>
              </DNABox>
            </DNABox>
            { /* BADGES */ }
            <DNABox spacing="sm" fill>
              {/* COVER TAG */}
              <Iffy is={isCover}>
                <DNAChip.Beacon variant="SLIDE_COVER" />
              </Iffy>
              {/* CAN INSERT TEXT TAG */}
              <Iffy is={isTextInsertionEnabled}>
                <DNAChip.Beacon variant="SLIDE_CAN_INSERT_TEXT" />
              </Iffy>
              {/* REQUIRED TAG */}
              <Iffy is={isRequired}>
                <DNAChip.Beacon variant="REQUIRED" />
              </Iffy>
            </DNABox>
          </Iffy>
        </DNABox>
      </InteractiveWrapper>
    );
  },
))

export const DNAThumbnailLazy: React.FC<
  DNAThumbnailProps &
  { parentRef?: React.RefObject<ScrollView> }
> = (props) => {
  const containerRef = useRef<HTMLElement>(null)
  const { parentRef, deferLoad } = props

  const isInView = useInView(
    deferLoad === undefined ? false : !deferLoad,
    containerRef,
    parentRef,
  )

  return (
    <DNABox ref={containerRef}>
      <DNAThumbnail
        {...props}
        deferLoad={deferLoad ?? !isInView}
      />
    </DNABox>
  )
}

export const DNAThumbnail: DNAThumbnail =
  Object.assign(
    {},
    DNAThumbnailCore,
    {
      displayName: 'DNAThumbnail',
      Modes: DNAThumbnailModes,
      Variants: DNAThumbnailVariants,
      MagnifyPresets: MagnifyPreset,
      Lazy: DNAThumbnailLazy,
    },
  )

export default DNAThumbnail
